/**
 * Utility for forced location-based country code detection via IP geolocation
 * (No browser permission popups). Saves detected prefix to localStorage on every visit.
 */

const STORAGE_KEY = 'thurent_user_country';

const DEFAULT_COUNTRY = {
  isoCode: 'IN',
  lowerIsoCode: 'in',
  countryCode: '+91',
};

/**
 * Forced IP-based location detection that runs whenever login/signup page is opened.
 * Detects country without showing any browser permission popups and updates localStorage.
 */
export const detectAndSaveUserCountry = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s safety timeout

    const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      const countryIso = (data.country_code || '').toUpperCase();

      let detected = DEFAULT_COUNTRY;
      if (countryIso === 'US' || countryIso === 'CA' || data.country_calling_code === '+1') {
        detected = {
          isoCode: 'US',
          lowerIsoCode: 'us',
          countryCode: '+1',
        };
      } else {
        detected = {
          isoCode: 'IN',
          lowerIsoCode: 'in',
          countryCode: '+91',
        };
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(detected));
      return detected;
    }
  } catch (err) {
    console.warn('IP location detection failed. Checking localStorage fallback:', err);
  }

  // Fallback to existing localStorage if API call fails/times out
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && parsed.isoCode && parsed.countryCode) {
        return parsed;
      }
    }
  } catch (e) {
    // ignore
  }

  // Default fallback
  localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_COUNTRY));
  return DEFAULT_COUNTRY;
};

/**
 * Helper to get currently stored country synchronously from localStorage.
 */
export const getStoredUserCountry = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // ignore
  }
  return DEFAULT_COUNTRY;
};
